import { Card, Heading } from '@dittolive/anvil'
import { graphql } from 'babel-plugin-relay/macro'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { useAppManagementContext } from 'scenes/appManagement/context'

import { TransferAppSectionQuery } from './__generated__/TransferAppSectionQuery.graphql'
import TransferAppDestination from './TransferAppDestination'
import TransferAppDestinationSelector from './TransferAppDestinationSelector'

export const query = graphql`
  query TransferAppSectionQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        viewerCanTransfer
        transferRequestDestination {
          ...TransferAppDestination_transferRequestDestination
        }
        tenant {
          ... on Organization {
            # eslint-disable-next-line relay/unused-fields
            id
            owner {
              user {
                firstName
                lastName
                email
              }
            }
          }
          ... on User {
            # eslint-disable-next-line relay/unused-fields
            id
          }
        }
      }
    }
  }
`

const TransferAppSection = () => {
  const { appId } = useAppManagementContext()
  const { viewer } = useLazyLoadQuery<TransferAppSectionQuery>(
    query,
    { appId },
    {
      fetchPolicy: 'store-and-network',
    },
  )

  const renderTransferContent = () => {
    if (viewer.appById?.transferRequestDestination) {
      return (
        <TransferAppDestination
          destination={viewer.appById.transferRequestDestination}
          canCancel={viewer.appById.viewerCanTransfer}
          appId={appId}
        />
      )
    }

    if (viewer.appById?.viewerCanTransfer) {
      return <TransferAppDestinationSelector appId={appId} />
    }

    const organizationOwner = viewer.appById?.tenant?.owner?.user
    const ownerName =
      organizationOwner?.firstName || organizationOwner?.lastName
        ? `${organizationOwner.firstName} ${organizationOwner.lastName}`.trim()
        : organizationOwner?.email

    return (
      <p>
        {`Use this section to transfer the database to a different user or
          organization. Only the organization owner, ${ownerName}, is
          allowed to transfer this database.`}
      </p>
    )
  }

  return (
    <Card className="shadow-none">
      <Card.Header>
        <Heading level={3}>Transfer database</Heading>
        <p className="text-foreground-subtle text-base">
          Transfer your database to another person or organization
        </p>
      </Card.Header>
      <Card.Body>{renderTransferContent()}</Card.Body>
    </Card>
  )
}

export default TransferAppSection
